const sourceOf = (image: HTMLImageElement) => image.currentSrc || image.src;

/**
 * Resolves once the image has loaded or given up. Ad previews retry a failed image against its raw URL, in the
 * error event or a render later, so a failure counts only once two frames pass with no new source loading.
 */
export function whenImageSettles(image: HTMLImageElement) {
  return new Promise<void>((resolve) => {
    const settle = () => {
      image.removeEventListener("load", settle);
      image.removeEventListener("error", onError);
      resolve();
    };
    const onError = () => {
      const failed = sourceOf(image);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (
            !image.isConnected ||
            (image.complete && sourceOf(image) === failed)
          )
            settle();
        }),
      );
    };
    image.addEventListener("load", settle);
    image.addEventListener("error", onError);
  });
}

/** The longest a cover waits for the new content's images before it lets go. */
const CONTENT_LOAD_TIMEOUT_MS = 5000;
/** How long a cover waits for the new content to show an image: ad markup lays out a frame or two late. */
export const IMAGE_APPEAR_MS = 150;

const after = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const nextFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

/**
 * Resolves once the new content's images have loaded or given up, leaving out those inside the `cover` held over it.
 * An ad lays out its markup a frame or two after the swap, so the wait first looks for images; content that shows
 * none by then has nothing left to paint.
 */
export async function whenContentSettles(
  root: HTMLElement,
  cover: HTMLElement,
  imageAppearMs: number,
) {
  const liveImages = () =>
    [...root.querySelectorAll("img")].filter((image) => !cover.contains(image));
  const giveUpAt = performance.now() + imageAppearMs;
  while (liveImages().length === 0) {
    if (performance.now() >= giveUpAt) return;
    await nextFrame();
  }
  const pending = liveImages().filter((image) => !image.complete);
  if (pending.length === 0) return;
  await Promise.race([
    Promise.all(pending.map(whenImageSettles)),
    after(CONTENT_LOAD_TIMEOUT_MS),
  ]);
}
